import React, { Suspense, useEffect } from 'react';
import { scrollToTop } from '../utils/scrollUtils';
import { useFacebookLazyLoading } from '../hooks/useFacebookLazyLoading';
import ContentSkeleton from './Skeletons/SkeletonComponents';

// Facebook-style loading component with skeleton
const FacebookStyleLoading = ({ skeletonType = 'product-grid', skeletonCount = 3 }) => (
  <div className="facebook-style-loading">
    <ContentSkeleton type={skeletonType} count={skeletonCount} />
  </div>
);

// Facebook-style Lazy wrapper component
const LazyWrapper = ({ 
  children, 
  skeletonType = 'product-grid',
  skeletonCount = 3,
  scrollToTopOnMount = true,
  loadingDelay = 800,
  staggerDelay = 100
}) => {
  const { elementRef, isLoading, showContent } = useFacebookLazyLoading({
    skeletonDuration: loadingDelay,
    staggerDelay: staggerDelay
  });

  useEffect(() => {
    if (scrollToTopOnMount && showContent) {
      // Scroll to top when content is ready
      setTimeout(() => scrollToTop('auto'), 100);
    }
  }, [scrollToTopOnMount, showContent]);

  return (
    <div ref={elementRef} className="facebook-lazy-wrapper">
      {isLoading ? (
        <FacebookStyleLoading 
          skeletonType={skeletonType} 
          skeletonCount={skeletonCount} 
        />
      ) : (
        <div className={showContent ? 'content-fade-in-after-skeleton' : 'content-loading'}>
          <Suspense fallback={
            <FacebookStyleLoading 
              skeletonType={skeletonType} 
              skeletonCount={skeletonCount} 
            />
          }>
            {children}
          </Suspense>
        </div>
      )}
    </div>
  );
};

// Higher-order component for lazy loading with scroll restoration
export const withLazyLoading = (Component, options = {}) => {
  const LazyComponent = React.lazy(() => {
    return new Promise(resolve => {
      const startTime = Date.now();
      
      // Ensure minimum loading time to prevent flash
      const minTime = options.minLoadingTime || 300;
      
      Promise.resolve({ default: Component }).then(module => {
        const elapsedTime = Date.now() - startTime;
        const remainingTime = Math.max(0, minTime - elapsedTime);
        
        setTimeout(() => {
          resolve(module);
        }, remainingTime);
      });
    });
  });

  return (props) => (
    <LazyWrapper 
      scrollToTopOnMount={options.scrollToTopOnMount !== false}
      fallback={options.fallback}
      minLoadingTime={options.minLoadingTime}
    >
      <LazyComponent {...props} />
    </LazyWrapper>
  );
};

export default LazyWrapper;
